import { NextSeo } from "next-seo";
import { Address } from "viem";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { formatAddress, useCurrentChainInfo } from "@looksrare/utils";
import { USER_ACCOUNT_URI } from "@looksrare/config";
import getDefaultSeo, { getCanonicalAndLanguageAlternates } from "config/seo";
import { safeGetAddress } from "utils/safeGetAddress";
import { User } from "types/graphql";
import { useAddressFromQuery } from "../hooks/useAddressFromQuery";

interface Props {
  user?: User;
}

const AccountSeo: React.FC<React.PropsWithChildren<Props>> = ({ user }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const address = useAddressFromQuery();
  const currentChainInfo = useCurrentChainInfo();

  const isMe = router.query.address === USER_ACCOUNT_URI;
  const queryAddress = router.query.address as string;
  const canonicalPathname = `/accounts/${address ? safeGetAddress(address || "") || address : USER_ACCOUNT_URI}`;

  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(
    canonicalPathname,
    router,
    currentChainInfo.appUrl
  );

  return (
    <>
      {isMe ? (
        <NextSeo
          noindex
          nofollow
          title={t("Your account")}
          openGraph={{
            title: t("{{pageTitle}} | LooksRare", { pageTitle: t("Your Account") }),
            url: canonical,
          }}
          canonical={canonical}
          languageAlternates={languageAlternates}
        />
      ) : (
        <NextSeo
          noindex
          nofollow
          title={user?.name || formatAddress(queryAddress as Address)}
          description={user?.biography || getDefaultSeo(t, currentChainInfo.appUrl).description}
          openGraph={{
            title: t("{{pageTitle}} | LooksRare", { pageTitle: user?.name || formatAddress(queryAddress as Address) }),
            description: user?.biography || getDefaultSeo(t, currentChainInfo.appUrl).description,
            url: canonical,
          }}
          canonical={canonical}
          languageAlternates={languageAlternates}
        />
      )}
    </>
  );
};

export default AccountSeo;
